// The signed-in person (GET /api/session, M2) and the permission checks every page uses.
//   useSession()                       the session; route guards make sure it's loaded
//   loadSession(queryClient, href)     for beforeLoad: 401 -> /sign-in?returnTo, not in a group -> /no-access
//   requireStaffSession(...)           for staff-only routes: reporters only -> /my-requests
//   canApp(session, appId, perm)       a permission in one app (permissions.byApp)
//   canTool(session, perm)             a tool-level permission
//   staffRoleLabel(session)            "Admin" or "Editor", the way the pages name a staff member's role
//   signOut()                          POST /auth/sign-out, then S1
// Staff are the Editors and Admins groups (poc CT-008): each holds the app role `owner` in every app, so
// permissions.byApp lists every app. After registering an app (M11), refetch ['session'] for the new app.
import type { PermissionCode } from '@feedback/shared/constants/roles';
import type { SessionView } from '@feedback/shared/contracts/identity';
import { type QueryClient, queryOptions, useSuspenseQuery } from '@tanstack/react-query';
import { redirect } from '@tanstack/react-router';
import { ApiError, api, hasCsrfToken, isApiError, setCsrfToken } from './api';

const SESSION_STALE_MS = 60_000;

export const sessionQuery = queryOptions({
  queryKey: ['session'],
  queryFn: async () => {
    const session = await api.get<SessionView>('/api/session');
    setCsrfToken(session.csrfToken);
    return session;
  },
  staleTime: SESSION_STALE_MS,
  retry: false,
});

export function useSession(): SessionView {
  return useSuspenseQuery(sessionQuery).data;
}

/** beforeLoad helper: the session, or a redirect to sign-in (keeping the page) or to no-access. */
export async function loadSession(queryClient: QueryClient, href: string): Promise<SessionView> {
  try {
    return await queryClient.ensureQueryData(sessionQuery);
  } catch (error) {
    if (isApiError(error) && error.status === 401) {
      throw redirect({ to: '/sign-in', search: { returnTo: href } });
    }
    if (isApiError(error) && error.status === 403) {
      // Not in any group: the 403 still carries the session's CSRF token, so S2 can sign out.
      if (typeof error.problem.csrfToken === 'string') {
        setCsrfToken(error.problem.csrfToken);
      }
      throw redirect({ to: '/no-access', search: {} });
    }
    throw error;
  }
}

/** beforeLoad helper for staff pages: people who can only report go to their own requests. */
export async function requireStaffSession(queryClient: QueryClient, href: string): Promise<SessionView> {
  const session = await loadSession(queryClient, href);
  if (!session.isStaff) {
    throw redirect({ to: '/my-requests' });
  }
  return session;
}

export function canApp(session: SessionView, appId: string, permission: PermissionCode): boolean {
  return session.permissions.byApp[appId]?.includes(permission) ?? false;
}

export function canTool(session: SessionView, permission: PermissionCode): boolean {
  return session.permissions.tool.includes(permission);
}

/** Dashboard (REQ-22.1): every Editor and Admin (poc CT-008). */
export function canSeeDashboard(session: SessionView): boolean {
  return session.isStaff;
}

/** AI reports (S11, M11): every Editor and Admin views and scores them (poc CT-008). */
export function canSeeAiReports(session: SessionView): boolean {
  return session.isStaff;
}

/** A staff member's role as the pages show it (poc CT-008): "Admin" for the Admins group, else "Editor". */
export function staffRoleLabel(session: SessionView): string {
  return session.isPlatformAdmin ? 'Admin' : 'Editor';
}

/** The CSRF token from GET /api/session; a 403 for someone in no group still carries it. */
async function fetchCsrfToken(): Promise<void> {
  try {
    setCsrfToken((await api.get<SessionView>('/api/session')).csrfToken);
  } catch (error) {
    if (!isApiError(error)) {
      throw error;
    }
    if (typeof error.problem.csrfToken === 'string') {
      setCsrfToken(error.problem.csrfToken);
    }
  }
}

/**
 * POST /auth/sign-out (CSRF), then the sign-in page, whatever the answer. Sign-out needs only the
 * session cookie and its CSRF token, so it also works on S2 for someone no longer in any group.
 */
export async function signOut(): Promise<void> {
  try {
    if (!hasCsrfToken()) {
      await fetchCsrfToken();
    }
    await api.post('/auth/sign-out');
  } catch (error) {
    // The server answers 204 to this JSON request; a refusal still ends here.
    if (!(error instanceof ApiError)) {
      throw error;
    }
  } finally {
    setCsrfToken(null);
    window.location.assign('/sign-in');
  }
}
